import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { HighlightOff as HighlightOffIcon, Search as SearchIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import CustomizedSnackbars from '../Notifications/SnackBar';
import { useBuscarAutorizaciones } from './hooks/useBuscarAutorizaciones';
import { TipoCoberturaSelector } from '../Common/TipoCoberturaSelector';
import AutorizacionesTableResult from './AutorizacionesTableResult';

const formObjectModel = {
  numeroDocumento: '',
  fechaDesde: '',
  fechaHasta: '',
  tipoCobertura: null,
};

export const BuscarAutorizaciones = () => {
  const [showForm, setShowForm] = useState(true);
  const { autorizaciones, messageStatus, isLoading, buscarAutorizaciones, cleanBusqueda } =
    useBuscarAutorizaciones();
  const {
    control,
    getValues,
    trigger,
    reset,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    values: formObjectModel,
  });

  const onHandleSubmitForm = (data) => {
    buscarAutorizaciones(data);
  };

  const onHandlerLimpiar = () => {
    reset();
    setShowForm(true);
  };

  const onHandlerCleanMessage = () => {
    setShowForm(true);
  };

  const onHandlerBackToForm = () => {
    cleanBusqueda();
    reset();
    setShowForm(true);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card sx={{ width: '100%', p: 1 }}>
          <CardContent>
            <Typography gutterBottom variant='h5' component='h2'>
              Buscar Solicitud de Autorización
            </Typography>
            <Typography
              variant='body2'
              color='textSecondary'
              component='p'
              align='justify'
              gutterBottom
            >
              Acá podrás buscar las solicitudes de autorización solicitadas según tipo de trámite,
              afiliado y/o en un rango de fecha.
            </Typography>
          </CardContent>

          {!autorizaciones && (
            <form onSubmit={handleSubmit((data) => onHandleSubmitForm(data))}>
              <CardContent>
                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <Controller
                    control={control}
                    name='numeroDocumento'
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TextField
                          label='Ingresá el dni del afiliado'
                          variant='standard'
                          fullWidth
                          value={field.value || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            field.onChange(newValue);
                            trigger('numeroDocumento');
                          }}
                          error={!!error}
                          helperText={error ? error.message : ''}
                        />
                      );
                    }}
                    rules={{
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'El dni debe contener sólo números',
                      },
                      maxLength: {
                        value: 10,
                        message: 'El dni de afiliado es demasiado largo',
                      },
                    }}
                  />
                </FormControl>
                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <Controller
                    control={control}
                    name='fechaDesde'
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TextField
                          type='date'
                          label='Desde'
                          variant='standard'
                          fullWidth
                          value={field?.value || ''}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger('fechaDesde');
                          }}
                          error={!!error}
                          helperText={error ? error.message : ''}
                        />
                      );
                    }}
                    rules={{
                      validate: (value) => {
                        const fechaHasta = getValues('fechaHasta');
                        // Si hay fechaHasta, validar que sea mayor o igual a fechaDesde
                        if (fechaHasta) {
                          // Forzar validación de fechaHasta para limpiar/informar errores
                          setTimeout(() => trigger('fechaHasta'), 0);
                        }
                        if (!value || !fechaHasta) {
                          return true;
                        }
                        if (value <= fechaHasta) {
                          return true;
                        } else if (fechaHasta < value) {
                          return 'La fecha desde debe ser menor o igual a la fecha hasta.';
                        }
                      },
                    }}
                  />
                </FormControl>
                <FormControl fullWidth={true} variant='outlined' margin='normal'>
                  <Controller
                    control={control}
                    name='fechaHasta'
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <TextField
                          type='date'
                          label='Hasta'
                          variant='standard'
                          fullWidth
                          value={field?.value || ''}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger('fechaHasta');
                          }}
                          error={!!error}
                          helperText={error ? error.message : ''}
                        />
                      );
                    }}
                    rules={{
                      validate: (value) => {
                        const fechaDesde = getValues('fechaDesde');
                        if (!value) {
                          if (fechaDesde) {
                            return 'La fecha hasta es obligatoria si se ingresó la fecha desde.';
                          }
                          return true;
                        }
                        if (value && !fechaDesde) {
                          return 'La fecha desde es obligatoria.';
                        }
                        if (fechaDesde <= value) {
                          return true;
                        } else if (fechaDesde > value) {
                          return 'La fecha hasta debe ser mayor o igual a la fecha desde.';
                        }
                      },
                    }}
                  />
                </FormControl>
                <FormControl fullWidth={true} variant='standard' margin='normal'>
                  <Controller
                    control={control}
                    name='tipoCobertura'
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <>
                          <TipoCoberturaSelector
                            value={field?.value || ''}
                            label={'Tipo de trámite'}
                            onChangeOption={(e) => {
                              field.onChange(e);
                              trigger('tipoCobertura');
                            }}
                          />
                        </>
                      );
                    }}
                  />
                </FormControl>
              </CardContent>

              <CardActions sx={{ px: 2 }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                      }}
                    >
                      <Button
                        variant='contained'
                        size='medium'
                        startIcon={<HighlightOffIcon />}
                        color='neutral'
                        onClick={onHandlerLimpiar}
                      >
                        Limpiar
                      </Button>
                      <LoadingButton
                        type='submit'
                        variant='contained'
                        color='primary'
                        loading={isLoading}
                        startIcon={<SearchIcon />}
                        disabled={!isDirty || !isValid}
                      >
                        Buscar
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              </CardActions>
            </form>
          )}
          {autorizaciones?.length > 0 && (
            <Grid container direction='column' justifyContent='space-between' spacing={1}>
              <Grid item>
                <AutorizacionesTableResult
                  autorizaciones={autorizaciones}
                  onRefreshTable={() => buscarAutorizaciones(getValues())}
                />
              </Grid>
              <Grid item>
                <Grid container justifyContent='flex-end' px={2} py={1} spacing={2}>
                  <Grid item>
                    <Button
                      variant='contained'
                      color='neutral'
                      startIcon={<SearchIcon />}
                      onClick={(e) => onHandlerBackToForm()}
                    >
                      Buscar Otras
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Card>
      </Grid>

      <CustomizedSnackbars
        open={messageStatus?.status}
        autoHideDuration={8000}
        severity={messageStatus?.status || 'success'}
        message={messageStatus?.message}
        onDeleteMessage={onHandlerCleanMessage}
      />
    </Grid>
  );
};
